import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  wget,
  get,
  find,
  isFunction,
  compact,
} from 'lodash';
import { Button } from '@unite-us/app-components';
import findOrCreateClientRelationship from 'src/api/core/ClientRelationship/findOrCreateClientRelationship';
import populatePreferredLanguages from 'src/api/core/People/populatePreferredLanguages';
import { SEARCH } from 'common/utils/EventTracker/utils/eventConstants';
import callOrLog from 'common/utils/callOrLog';
import {
  updateGroupContact,
  addContactToGroup,
} from 'actions/Contact/Group';
import mergeContactWithRequestorDetails from 'src/components/Contacts/utils/mergeContactWithRequestorDetails';
import ContactCard from './ContactCard';
import {
  goToAddInformationStep,
  decodeContactData,
} from '../utils';

const normalizeNamePart = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const normalized = value.toString().trim().toLowerCase();
  return normalized || undefined;
};

const normalizeDateValue = (value) => {
  if ([undefined, null, ''].includes(value)) {
    return undefined;
  }
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? undefined : numericValue;
};

const formatDateOfBirth = (timestamp) => {
  if (!timestamp) {
    return '';
  }
  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.valueOf())) {
    return '';
  }
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

const buildSearchSummary = ({ firstName, lastName, dateOfBirthFormatted }) => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (dateOfBirthFormatted) {
    return `You searched for ${fullName || 'this client'}, born ${dateOfBirthFormatted}.`;
  }
  if (fullName) {
    return `You searched for ${fullName}.`;
  }
  return 'You searched for matching client records.';
};

const buildSearchCriteria = ({ firstName, lastName, dateOfBirth }) => ({
  firstName: normalizeNamePart(firstName),
  lastName: normalizeNamePart(lastName),
  dateOfBirth: normalizeDateValue(dateOfBirth),
});

const hasSearchCriteria = (criteria) => Object.values(criteria).some((value) => value !== undefined);

const matchField = (searchValue, contactValue) => (
  searchValue !== undefined ? contactValue === searchValue : undefined
);

const evaluateMatches = (matches) => {
  const definedMatches = matches.filter((value) => value !== undefined);
  if (!definedMatches.length) {
    return undefined;
  }
  return definedMatches.every(Boolean);
};

const getContactNamePart = (contact = {}, key) => {
  if (contact[key]) {
    return contact[key];
  }
  if (contact.person && contact.person[key]) {
    return contact.person[key];
  }
  if (contact.full_name) {
    const parts = contact.full_name.split(' ').filter(Boolean);
    if (!parts.length) {
      return undefined;
    }
    if (key === 'first_name') {
      return parts[0];
    }
    if (key === 'last_name') {
      return parts.slice(1).join(' ') || undefined;
    }
  }
  return undefined;
};

const buildMatchDetails = (contact, searchCriteria, hasCriteria) => {
  const contactFirstName = normalizeNamePart(getContactNamePart(contact, 'first_name'));
  const contactLastName = normalizeNamePart(getContactNamePart(contact, 'last_name'));
  const contactDateOfBirth = normalizeDateValue(contact?.date_of_birth ?? contact?.person?.date_of_birth);

  const firstNameMatch = matchField(searchCriteria.firstName, contactFirstName);
  const lastNameMatch = matchField(searchCriteria.lastName, contactLastName);
  const dateOfBirthMatch = matchField(searchCriteria.dateOfBirth, contactDateOfBirth);

  const overallMatch = evaluateMatches([firstNameMatch, lastNameMatch, dateOfBirthMatch]);

  return {
    hasCriteria,
    overall: overallMatch,
    fields: {
      firstName: firstNameMatch,
      lastName: lastNameMatch,
      dateOfBirth: dateOfBirthMatch,
    },
  };
};

export class ContactConfirmation extends Component {
  constructor(props) {
    super(props);

    this.onCreateNewContact = this.onCreateNewContact.bind(this);
    this.onSelectContact = this.onSelectContact.bind(this);
  }

  onCreateNewContact() {
    const {
      activeStep,
      contacts,
      context,
      location: { query: { contact } },
    } = this.props;
    const contactData = decodeContactData(contact);

    // this is usually called when a user ignores the existing records and proceeds to create new client
    callOrLog(() => this.context.eventTracker(SEARCH.ignoredRecords, {
      contacts: contacts.length,
    }));

    if (isFunction(activeStep.onConfirmationComplete)) {
      return activeStep.onConfirmationComplete({ formData: contactData });
    }
    return goToAddInformationStep({ context, contactData });
  }

  onSelectContact(contact) {
    return async () => {
      const {
        activeStep,
        assistanceRequest,
        context,
        groupId,
        requestor,
        location: { query },
      } = this.props;
      const arId = get(assistanceRequest, 'id');
      // if there is a contact and a contact id, proceed
      if (contact.id && contact?.person !== null) {
        const contactId = contact.person?.id;

        await findOrCreateClientRelationship(contactId, groupId);

        callOrLog(() => this.context.eventTracker(SEARCH.useRecord[context], {
          contact_id: contact.id,
        }));

        const coreContactResponse = await this.props.addContactToGroup(contactId);
        const coreContact = get(coreContactResponse, 'data.data', {});
        await populatePreferredLanguages(coreContact);

        const mergedContactFomAR = mergeContactWithRequestorDetails(coreContact, requestor);
        await this.props.updateGroupContact(groupId, contactId, mergedContactFomAR, {
          showNotification: false,
          assistance_request_id: arId,
          forceClientRelationship: false,
        });

        if (isFunction(activeStep.onConfirmationComplete)) {
          return activeStep.onConfirmationComplete({ contactId });
        }

        return goToAddInformationStep({
          contactData: decodeContactData(query.contact),
          contactId,
          context,
        });
      }
      // let's track that we are selecting an external contact that is not linked to a uu contact
      callOrLog(() => this.context.eventTracker(SEARCH.useRecordNoId[context]));
      const mergedContactFomAR = mergeContactWithRequestorDetails(contact, requestor);
      // Because the addresses of mergedContactFomAR are only the new ones, we need to concatemate the existing ones
      const mergedAddresses = compact([
        ...contact.addresses || [],
        ...mergedContactFomAR.addresses || [],
      ]);
      const contactData = {
        ...contact,
        ...mergedContactFomAR,
        addresses: mergedAddresses,
      };
      if (isFunction(activeStep.onConfirmationComplete)) {
        return activeStep.onConfirmationComplete({ formData: contactData, fromIntegration: true });
      }
      // in this case, the contact is coming from nextgate.
      return goToAddInformationStep({ context, contactData, fromIntegration: true });
    };
  }

  render() {
    const {
      contacts,
      location,
    } = this.props;

    const encodedContactFromQuery = location.query.contact;
    const decodedSearchFormData = decodeContactData(encodedContactFromQuery);

    const {
      first_name: firstName,
      last_name: lastName,
      date_of_birth: rawDateOfBirth,
    } = decodedSearchFormData || {};

    const dateOfBirthFormatted = formatDateOfBirth(rawDateOfBirth);
    const searchSummary = buildSearchSummary({ firstName, lastName, dateOfBirthFormatted });
    const searchCriteria = buildSearchCriteria({ firstName, lastName, dateOfBirth: rawDateOfBirth });
    const searchCriteriaProvided = hasSearchCriteria(searchCriteria);

    return (
      <div className="contact-confirmation">
        <section
          aria-labelledby="contact-select-heading"
          aria-describedby="contact-search-summary contact-select-description"
          className="contact-header-confirmation focus-outline"
          role="region"
          tabIndex={0}
        >
          <h2 id="contact-select-heading" className="font-heavy-font">Select a Record</h2>
          <p
            className="contact-search-summary m-0 text-center"
            id="contact-search-summary"
          >
            {searchSummary}
          </p>
          <p id="contact-select-description">
            We found {contacts.length} records that might be a match. If one of the existing records is your client,
            click Use This Record. Click Create New Client to create a new record.
          </p>
        </section>
        <section
          aria-labelledby="existing-records-heading"
          className="existing-records-region focus-outline my-[51px] mx-0"
          role="region"
          tabIndex={0}
        >
          <h3
            id="existing-records-heading"
            className="text-text-blue text-center font-regular-font text-lg font-semibold leading-7 mb-4"
          >
            Existing Records
          </h3>
          <ul className="contact-list list-unstyled flex flex-row flex-wrap justify-evenly">
            {
              contacts.map((contact, index) => {
                const matchDetails = buildMatchDetails(contact, searchCriteria, searchCriteriaProvided);
                const cardKey = contact.id || `${contact.first_name}-${contact.last_name}-${index}`;
                const cardId = `contact-card-${contact.id || index}`;

                return (
                  <ContactCard
                    key={cardKey}
                    id={cardId}
                    contact={contact}
                    onSelectContact={this.onSelectContact}
                    indexNumber={index}
                    matchDetails={matchDetails}
                  />
                );
              })
            }
          </ul>
        </section>
        <p className="text-center p-4 focus-outline" role="note" tabIndex={0}>
          If none of the client records are your client, create a new one.
        </p>
        <div className="text-center">
          <Button
            id="create-new-client"
            className="create-new-client-btn"
            onClick={this.onCreateNewContact}
            label="Create New Client"
            secondary
            ariaExpanded={null}
          />
        </div>
      </div>
    );
  }
}

ContactConfirmation.propTypes = {
  activeStep: PropTypes.object,
  addContactToGroup: PropTypes.func.isRequired,
  assistanceRequest: PropTypes.object.isRequired,
  contacts: PropTypes.array.isRequired,
  context: PropTypes.string,
  groupId: PropTypes.string.isRequired,
  location: PropTypes.shape({
    query: PropTypes.shape({
      contact: PropTypes.string,
    }).isRequired,
  }).isRequired,
  requestor: PropTypes.object.isRequired,
  updateGroupContact: PropTypes.func.isRequired,
};

ContactConfirmation.defaultProps = {
  activeStep: {},
  context: null,
};

ContactConfirmation.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const requestId = get(ownProps, 'params.assistance_request_id');
  const requests = get(state, 'assistanceRequests.requests', []);
  const assistanceRequest = find(requests, { id: requestId }) || {};
  const requestor = get(assistanceRequest, 'requestor', {});
  const groupId = wget(state, 'session.groupId', '');

  return {
    assistanceRequest,
    contacts: wget(state, 'searchedContacts', []),
    groupId,
    requestor,
  };
}

export default connect(
  mapStateToProps,
  {
    addContactToGroup,
    updateGroupContact,
  },
)(ContactConfirmation);
